import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCmsContent } from '../../redux/manageCms/actionCreator';

/**
 * Module-level in-memory cache for CMS content.
 * Survives re-renders and SPA navigation; resets only on full tab close.
 * Key: CMS key string   →   Value: resolved CMS value string
 */
const cmsCache = {};

/**
 * Tracks in-flight requests to prevent duplicate concurrent fetches for the
 * same key (de-duplication).
 */
const pendingRequests = {};

// ---------------------------------------------------------------------------
// Hydrate cache from sessionStorage on module load.
// This means a page refresh does NOT re-trigger CMS loaders —
// previously fetched values are available immediately.
// ---------------------------------------------------------------------------
const STORAGE_KEY = 'hg_onb_cms_v1';
try {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) Object.assign(cmsCache, JSON.parse(stored));
} catch {}

const persistCache = () => {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cmsCache)); } catch {}
};

// ---------------------------------------------------------------------------
// Global CMS loading observable
// Counts how many distinct CMS batches are currently in-flight.
// ---------------------------------------------------------------------------
let cmsActiveCount = 0;
const cmsLoadingListeners = new Set();

export const subscribeCmsLoading = (listener) => {
  cmsLoadingListeners.add(listener);
  listener(cmsActiveCount > 0);
  return () => cmsLoadingListeners.delete(listener);
};

export const getCmsLoadingState = () => cmsActiveCount > 0;

const updateCmsActive = (delta) => {
  cmsActiveCount += delta;
  const isLoading = cmsActiveCount > 0;
  cmsLoadingListeners.forEach((fn) => fn(isLoading));
};

// ---------------------------------------------------------------------------
// Helper: write a batch result into cmsCache and persist to sessionStorage.
// Shared by both useCMS hook and prefetchCmsKeys.
// ---------------------------------------------------------------------------
const writeResultToCache = (result, uncachedKeys) => {
  const payload = (result && Array.isArray(result.result)) ? result.result : result;
  if (payload && typeof payload === 'object') {
    const items = Array.isArray(payload)
      ? payload
      : Object.entries(payload).map(([k, v]) => ({ key: k, value: v }));
    items.forEach((item) => {
      if (item && item.key) cmsCache[item.key] = item.value ?? '';
    });
  }
  uncachedKeys.forEach((k) => { if (!(k in cmsCache)) cmsCache[k] = ''; });
  persistCache();
};

// ---------------------------------------------------------------------------
// prefetchCmsKeys
// Silently fetches CMS keys for the NEXT screen in the background.
// Does NOT update cmsActiveCount — so no loading spinner is shown.
// Call this from OnboardingLayout when the user is on the current screen so
// that by the time they reach the next screen the cache is warm.
// ---------------------------------------------------------------------------
export const prefetchCmsKeys = (dispatch, keys = []) => {
  if (!keys || keys.length === 0) return;

  const uncachedKeys = keys.filter((k) => !(k in cmsCache));
  if (uncachedKeys.length === 0) return; // all already cached

  const batchKey = [...uncachedKeys].sort().join('|');
  if (pendingRequests[batchKey]) return; // already in-flight

  const promise = dispatch(fetchCmsContent({ keys: uncachedKeys }))
    .then((result) => writeResultToCache(result, uncachedKeys))
    .catch(() => {
      uncachedKeys.forEach((k) => { if (!(k in cmsCache)) cmsCache[k] = ''; });
      persistCache();
    })
    .finally(() => { delete pendingRequests[batchKey]; });

  pendingRequests[batchKey] = promise;
};

// ---------------------------------------------------------------------------
// useCMS hook
// ---------------------------------------------------------------------------
const useCMS = (keys = []) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [, forceRender] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!keys || keys.length === 0) return;

    const uncachedKeys = keys.filter((k) => !(k in cmsCache));
    if (uncachedKeys.length === 0) return; // all keys already in cache — no loader

    const batchKey = [...uncachedKeys].sort().join('|');

    if (pendingRequests[batchKey]) {
      pendingRequests[batchKey].then(() => {
        if (mountedRef.current) forceRender((n) => n + 1);
      });
      return;
    }

    setLoading(true);
    updateCmsActive(+1);

    const promise = dispatch(fetchCmsContent({ keys: uncachedKeys }))
      .then((result) => writeResultToCache(result, uncachedKeys))
      .catch(() => {
        uncachedKeys.forEach((k) => { if (!(k in cmsCache)) cmsCache[k] = ''; });
        persistCache();
      })
      .finally(() => {
        delete pendingRequests[batchKey];
        updateCmsActive(-1);
        if (mountedRef.current) {
          setLoading(false);
          forceRender((n) => n + 1);
        }
      });

    pendingRequests[batchKey] = promise;
  }, [keys.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const cms = (key, fallback = '') => {
    const raw = (key in cmsCache && cmsCache[key] !== '' ? cmsCache[key] : fallback).trim();
    const unwrapped = /^<p\b[^>]*>([\s\S]*?)<\/p>$/i.test(raw)
      ? raw.replace(/^<p\b[^>]*>([\s\S]*?)<\/p>$/i, '$1').trim()
      : raw;
    return unwrapped.replace(/<p\b([^>]*)>/g, (match, attrs) =>
      /class="([^"]*)"/.test(attrs)
        ? match.replace(/class="([^"]*)"/, 'class="$1 mb-0"')
        : `<p${attrs} class="mb-0">`,
    );
  };

  return { cms, loading };
};

export default useCMS;
